/**
 * 来場者イベントサービス
 * 公開イベント検索を提供します
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError } from '../../../shared/utils/logger';

/** イベント検索入力不正エラーコード */
export const VISITOR_EVENT_SEARCH_INVALID_INPUT_CODE = 'VISITOR_EVENT_SEARCH_INVALID_INPUT';

/** イベント検索失敗エラーコード */
export const VISITOR_EVENT_SEARCH_FAILED_CODE = 'VISITOR_EVENT_SEARCH_FAILED';

/** 企画別獲得pt取得失敗エラーコード */
export const VISITOR_EVENT_POINT_RULES_FAILED_CODE = 'VISITOR_EVENT_POINT_RULES_FAILED';

/** イベント一覧1ページの取得件数 */
const EVENT_PAGE_SIZE = 200;

/**
 * イベント行を検証する
 * @param {any} eventRow - イベント行
 * @returns {{
 * eventId: string,
 * title: string,
 * description: string,
 * location: string,
 * category: string,
 * startAt: string | null,
 * endAt: string | null,
 * searchKeywords: string[],
 * }} 検証済みイベント
 * @throws {AppError} 形式不正の場合
 */
const validateEventRow = (eventRow) => {
  /** イベントID */
  const eventId = eventRow?.id;

  /** タイトル */
  const title = eventRow?.title;

  /** 説明 */
  const description = eventRow?.description ?? '';

  /** 場所 */
  const location = eventRow?.location ?? '';

  /** カテゴリ */
  const category = eventRow?.category ?? '';

  /** 開始日時 */
  const startAt = eventRow?.start_at ?? null;

  /** 終了日時 */
  const endAt = eventRow?.end_at ?? null;

  /** 検索キーワード */
  const searchKeywords = Array.isArray(eventRow?.search_keywords) ? eventRow.search_keywords : [];

  if (!eventId || !title) {
    throw new AppError('events のレスポンス形式が不正です。', {
      code: VISITOR_EVENT_SEARCH_FAILED_CODE,
      statusCode: 500,
      details: { eventRow },
    });
  }

  return {
    eventId,
    title,
    description,
    location,
    category,
    startAt,
    endAt,
    searchKeywords,
  };
};

/**
 * 公開イベントを検索する
 * @param {string} [keyword=''] - 検索キーワード
 * @returns {Promise<Array<{
 * eventId: string,
 * title: string,
 * description: string,
 * location: string,
 * category: string,
 * startAt: string | null,
 * endAt: string | null,
 * searchKeywords: string[],
 * }>>} イベント一覧
 * @throws {AppError} 検索に失敗した場合
 */
export const visitorSearchEvents = async (keyword = '') => {
  if (typeof keyword !== 'string') {
    throw new AppError('検索キーワードの形式が不正です。', {
      code: VISITOR_EVENT_SEARCH_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  /** 正規化済みキーワード */
  const normalizedKeyword = keyword.trim();

  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** エスケープ済みキーワード */
  const escapedKeyword = normalizedKeyword.replaceAll('%', '\\%').replaceAll(',', '\\,');
  /** 全ページのイベント行 */
  const eventRows = [];
  /** 次ページ開始位置 */
  let pageStart = 0;

  while (true) {
    /** イベント検索クエリ */
    let eventsQuery = supabaseClient
      .from('events')
      .select('id, title, description, location, category, start_at, end_at, search_keywords')
      .eq('status', 'published')
      .order('start_at', { ascending: true })
      .order('id', { ascending: true })
      .range(pageStart, pageStart + EVENT_PAGE_SIZE - 1);

    if (normalizedKeyword) {
      eventsQuery = eventsQuery.or(
        `title.ilike.%${escapedKeyword}%,description.ilike.%${escapedKeyword}%,location.ilike.%${escapedKeyword}%,category.ilike.%${escapedKeyword}%`,
      );
    }

    /** ページ取得結果 */
    const { data, error } = await eventsQuery;

    if (error) {
      logError('events 検索に失敗しました。', {
        code: error.code,
        message: error.message,
        details: error.details,
        normalizedKeyword,
        pageStart,
      });

      throw new AppError('イベント一覧の取得に失敗しました。', {
        code: VISITOR_EVENT_SEARCH_FAILED_CODE,
        statusCode: 500,
        details: {
          message: error.message,
        },
      });
    }

    if (!Array.isArray(data)) {
      throw new AppError('events のレスポンス形式が不正です。', {
        code: VISITOR_EVENT_SEARCH_FAILED_CODE,
        statusCode: 500,
        details: { data },
      });
    }

    eventRows.push(...data);

    if (data.length < EVENT_PAGE_SIZE) {
      break;
    }

    pageStart += EVENT_PAGE_SIZE;
  }

  return eventRows.map(validateEventRow);
};

/**
 * 公開中の企画に紐づく実効ptを取得する。旧DBでは従来のルールへ戻す。
 * 新RPCは付与処理と同じ期間限定調整と企画QRの全体倍率を反映した値を返す。RPC未導入のDBだけ従来の
 * 公開`event_point_rules`を使い、複数行がある場合は従来どおり最大値を採用する（全体倍率は反映しない）。
 * @returns {Promise<Record<string, number>>} 企画IDをキーにした付与pt
 * @throws {AppError} 取得に失敗した場合
 */
export const visitorGetActiveEventPointRuleMaxPoints = async (): Promise<Record<string, number>> => {
  /** Supabaseクライアント */
  const supabaseClient = getSupabaseClient();

  /** 公開企画の実効ptを200件ずつ取得する。 */
  let data: Array<{ event_id: string; points: number }> = [];
  let error: { code?: string; message: string; details?: string } | null = null;
  for (let offset = 0; ; offset += EVENT_PAGE_SIZE) {
    const page = await supabaseClient.rpc('visitor_event_effective_points').range(offset, offset + EVENT_PAGE_SIZE - 1);
    if (offset === 0 && (page.error?.code === 'PGRST202' || page.error?.code === '42883')) {
      // 新しい来場者アプリをDB migrationより先に公開できるよう、RPC未導入時だけ旧経路を使う。
      const fallback = await supabaseClient.from('event_point_rules').select('event_id, points').eq('is_active', true);
      data = fallback.data ?? [];
      error = fallback.error;
      break;
    }
    error = page.error;
    if (error) break;
    data.push(...(page.data ?? []));
    if (!page.data || page.data.length < EVENT_PAGE_SIZE) break;
  }

  if (error) {
    logError('event_point_rules の取得に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    throw new AppError('企画別の獲得ptの取得に失敗しました。', {
      code: VISITOR_EVENT_POINT_RULES_FAILED_CODE,
      statusCode: 500,
      details: {
        message: error.message,
      },
    });
  }

  /** 企画IDごとの最大付与pt */
  const maxPointsByEventId: Record<string, number> = {};

  for (const ruleRow of Array.isArray(data) ? data : []) {
    /** 企画ID */
    const eventId = ruleRow?.event_id;
    /** 付与pt */
    const points = Number(ruleRow?.points);

    if (typeof eventId !== 'string' || !eventId || !Number.isFinite(points)) {
      continue;
    }

    if (!(eventId in maxPointsByEventId) || points > maxPointsByEventId[eventId]) {
      maxPointsByEventId[eventId] = points;
    }
  }

  return maxPointsByEventId;
};
